import { useState } from 'react'
import type { ReactElement } from 'react'
import { Link, useParams } from 'react-router-dom'

import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  InputText,
  Select,
  Spinner,
  Table,
  Tag,
} from '../../components'
import type { SelectOption, TableColumn } from '../../components'
import { FreshnessBadge } from '../../layout/FreshnessBadge'
import { getErrorCode } from '../../lib/errorCode'
import { GROUPS_PATH } from '../../lib/routes'
import { trpcMessage } from '../../lib/trpcMessages'
import { MIRROR_REFETCH_MS, trpc } from '../../trpc'
import type { RouterOutputs } from '../../trpc'
import styles from './GroupDetailPage.module.scss'

type StationGroup = RouterOutputs['stations']['listGroups'][number]
type ResolvedType = RouterOutputs['stations']['getGroup']['types'][number]
type CatalogType = RouterOutputs['stations']['listWorkCenterTypes']['types'][number]
type ResolvedStation = RouterOutputs['stations']['getGroup']['stations'][number]
type CatalogMachine = RouterOutputs['stations']['listMachines']['machines'][number]
type Freshness = RouterOutputs['stations']['getGroup']['freshness']

/** The API's own cap on `name`, so a too-long name is not a round-trip. */
const NAME_MAX_LENGTH = 120

const DAY_MONTH_YEAR = new Intl.DateTimeFormat('es-ES', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

/** `code` and `title` are nullable in the mirror's own DDL — both, on any row. */
const mirrorLabel = (code: string | null, title: string | null, fallback: string): string => {
  const parts = [code, title].filter((part): part is string => part !== null && part !== '')
  return parts.length > 0 ? parts.join(' · ') : fallback
}

/**
 * What a type of this group is called on screen.
 *
 * ⚠️ `known: false` is an ORPHAN: MARS reloaded the mirror and the type is gone.
 * It is named and kept in the list — never hidden — because the only way out is
 * a manager removing it, and a row nobody can see is a row nobody removes.
 */
const typeLabel = (type: ResolvedType): string =>
  type.known ? mirrorLabel(type.code, type.title, `Tipo ${type.typeId}`) : `Tipo desconocido (id ${type.typeId})`

/**
 * What a machine of this group is called on screen. `known: false` is not
 * always a machine that is gone: the mirror only lists machines some recent MO
 * used, so it stays named and removable, like an orphaned type.
 */
const stationLabel = (station: ResolvedStation): string =>
  station.known ? mirrorLabel(station.code, station.title, `Máquina ${station.stationId}`) : `Máquina desconocida (id ${station.stationId})`

/**
 * One station group: its name and state, the work-center types and the
 * machines hung on it, and who is assigned to it right now.
 *
 * The types come from the mirror through a LEFT JOIN, so this screen carries the
 * freshness badge next to them: the data is up to two minutes behind and a plant
 * front that hides that gets somebody to trust a state that is no longer true.
 */
export const GroupDetailPage = (): ReactElement => {
  const { id } = useParams<{ id: string }>()
  // The route pattern is `/grupos/:id`, so the param is always there; `?? ''`
  // exists only because `useParams` cannot know that, and an empty id is a
  // BAD_REQUEST that surfaces like any other failure.
  const stationGroupId = id ?? ''

  // Polled: the types are resolved against the mirror, which MARS reloads, so a
  // screen left open would keep showing types that no longer exist as if they did.
  const groupQuery = trpc.stations.getGroup.useQuery(
    { stationGroupId },
    { refetchInterval: MIRROR_REFETCH_MS },
  )

  if (groupQuery.isPending) {
    return <Spinner size="page" label="Cargando el grupo…" />
  }

  if (groupQuery.isError) {
    return (
      <section className={styles['group-detail']}>
        <Link className={styles['group-detail__back']} to={GROUPS_PATH}>← Grupos</Link>
        {getErrorCode(groupQuery.error) === 'NOT_FOUND'
          // Its own state, and not the generic error: a group deleted or
          // renamed while the tab was open is not a failure to retry.
          ? (
            <EmptyState
              title="Este grupo ya no existe"
              message="Puede que se haya eliminado mientras la pantalla estaba abierta."
              action={<Link className={styles['group-detail__back']} to={GROUPS_PATH}>Volver a grupos</Link>}
            />
          )
          : <ErrorState error={groupQuery.error} onRetry={() => void groupQuery.refetch()} />}
      </section>
    )
  }

  const { group, types, stations, freshness } = groupQuery.data

  return (
    <section className={styles['group-detail']}>
      <header className={styles['group-detail__header']}>
        <Link className={styles['group-detail__back']} to={GROUPS_PATH}>← Grupos</Link>
        <h1 className={styles['group-detail__title']}>{group.name}</h1>
        {group.active ? <Tag tone="ok" label="Activo" /> : <Tag tone="warn" label="Inactivo" />}
      </header>

      <GroupSettings group={group} />
      <GroupTypes stationGroupId={group.id} types={types} freshness={freshness} />
      <GroupStations stationGroupId={group.id} stations={stations} />
      <GroupAssignees stationGroupId={group.id} />
    </section>
  )
}

type GroupSettingsProps = {
  group: StationGroup
}

/**
 * The group's own two fields. A sub-component so the name input can be seeded
 * from the loaded row by `useState` instead of an effect syncing a form to a
 * query — this only mounts once the group is there.
 */
const GroupSettings = ({ group }: GroupSettingsProps): ReactElement => {
  const utils = trpc.useUtils()
  const [name, setName] = useState(group.name)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [confirmDeactivate, setConfirmDeactivate] = useState(false)

  const updateGroup = trpc.stations.updateGroup.useMutation({
    onSuccess: () => {
      void utils.stations.getGroup.invalidate({ stationGroupId: group.id })
      // The list carries the name and the state too, so it is stale now.
      void utils.stations.listGroups.invalidate()
      setConfirmDeactivate(false)
    },
    onError: (error) => {
      setErrorMessage(trpcMessage(error))
      setConfirmDeactivate(false)
    },
  })

  const trimmed = name.trim()
  const nameUnchanged = trimmed === group.name

  return (
    <article className={styles['group-detail__card']}>
      <h2 className={styles['group-detail__card-title']}>Datos del grupo</h2>

      <form
        className={styles['group-detail__row']}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          setErrorMessage(null)
          updateGroup.mutate({ stationGroupId: group.id, name: trimmed })
        }}
      >
        <InputText
          className={styles['group-detail__grow']}
          label="Nombre"
          value={name}
          maxLength={NAME_MAX_LENGTH}
          onChange={(event) => setName(event.target.value)}
          required
        />
        <Button
          type="submit"
          // An unchanged name is a 200 that moves `updated_at` and says
          // "guardado" without having saved anything.
          disabled={updateGroup.isPending || trimmed === '' || nameUnchanged}
          label={updateGroup.isPending ? 'Guardando…' : 'Guardar nombre'}
        />
      </form>

      <div className={styles['group-detail__row']}>
        <p className={styles['group-detail__note']}>
          {group.active
            ? 'Un grupo inactivo deja de ofrecerse para asignar personas. No se borra nada.'
            : 'Este grupo está inactivo: no se puede asignar a nadie hasta que se reactive.'}
        </p>
        {group.active
          ? (
            <Button
              variant="danger"
              disabled={updateGroup.isPending}
              onClick={() => {
                setErrorMessage(null)
                setConfirmDeactivate(true)
              }}
              label="Desactivar grupo"
            />
          )
          : (
            <Button
              disabled={updateGroup.isPending}
              onClick={() => {
                setErrorMessage(null)
                updateGroup.mutate({ stationGroupId: group.id, active: true })
              }}
              label="Activar grupo"
            />
          )}
      </div>

      {errorMessage !== null && (
        <p role="alert" className={styles['group-detail__error']}>{errorMessage}</p>
      )}

      <ConfirmDialog
        open={confirmDeactivate}
        variant="danger"
        title="Desactivar el grupo"
        message={`«${group.name}» dejará de ofrecerse para asignar personas. Las asignaciones que ya existen no se cierran.`}
        confirmLabel="Desactivar"
        isLoading={updateGroup.isPending}
        onConfirm={() => updateGroup.mutate({ stationGroupId: group.id, active: false })}
        onCancel={() => setConfirmDeactivate(false)}
      />
    </article>
  )
}

type GroupTypesProps = {
  stationGroupId: string
  types: Array<ResolvedType>
  freshness: Freshness
}

/**
 * The work-center types the group covers — what plant calls "estaciones".
 *
 * A group covers SEVERAL on purpose: some routing steps can be done on a machine
 * of a different type than planned (a smaller pot), and the group is what
 * bounds what the operator may pick.
 */
const GroupTypes = ({ stationGroupId, types, freshness }: GroupTypesProps): ReactElement => {
  const utils = trpc.useUtils()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [pendingRemoval, setPendingRemoval] = useState<ResolvedType | null>(null)

  const invalidate = (): void => {
    void utils.stations.getGroup.invalidate({ stationGroupId })
    // The catalog carries who holds each type: this move changed that.
    void utils.stations.listWorkCenterTypes.invalidate()
  }

  const removeType = trpc.stations.removeType.useMutation({
    onSuccess: () => {
      invalidate()
      setPendingRemoval(null)
    },
    onError: (error) => {
      setErrorMessage(trpcMessage(error))
      setPendingRemoval(null)
    },
  })

  const columns: Array<TableColumn<ResolvedType>> = [
    {
      key: 'type',
      header: 'Tipo de estación',
      render: (type) => (
        <span className={styles['group-detail__type']}>
          {typeLabel(type)}
          {!type.known && <Tag tone="warn" label="Ya no existe en el MRP" />}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Acciones',
      align: 'right',
      render: (type) => (
        <Button
          variant="danger"
          // The accessible name says WHICH type: four "Quitar" buttons in a
          // column are four ways to remove the wrong one.
          aria-label={`Quitar ${typeLabel(type)}`}
          disabled={removeType.isPending}
          onClick={() => {
            setErrorMessage(null)
            setPendingRemoval(type)
          }}
          label="Quitar"
        />
      ),
    },
  ]

  return (
    <article className={styles['group-detail__card']}>
      <div className={styles['group-detail__card-header']}>
        <h2 className={styles['group-detail__card-title']}>Tipos de estación</h2>
        <FreshnessBadge freshness={freshness} />
      </div>

      {types.length === 0
        ? (
          <EmptyState
            title="Este grupo no cubre ningún tipo todavía"
            message="Añade abajo los tipos de estación entre los que podrá elegir el operario."
          />
        )
        : (
          <Table
            caption="Tipos de estación del grupo"
            columns={columns}
            rows={types}
            rowKey={(type) => String(type.typeId)}
          />
        )}

      {errorMessage !== null && (
        <p role="alert" className={styles['group-detail__error']}>{errorMessage}</p>
      )}

      <AddTypeForm
        stationGroupId={stationGroupId}
        ownTypeIds={new Set(types.map((type) => type.typeId))}
        onSaved={invalidate}
      />

      <ConfirmDialog
        open={pendingRemoval !== null}
        variant="danger"
        title="Quitar el tipo del grupo"
        message={
          pendingRemoval === null
            ? ''
            : `«${typeLabel(pendingRemoval)}» dejará de estar en este grupo. Se puede volver a añadir.`
        }
        confirmLabel="Quitar"
        isLoading={removeType.isPending}
        onConfirm={() => {
          if (pendingRemoval !== null) {
            removeType.mutate({ stationGroupId, typeId: pendingRemoval.typeId })
          }
        }}
        onCancel={() => setPendingRemoval(null)}
      />
    </article>
  )
}

type AddTypeFormProps = {
  stationGroupId: string
  /** What this group already covers — offering it again only earns a CONFLICT. */
  ownTypeIds: Set<number>
  onSaved: () => void
}

/**
 * Adding a type from the mirror's catalog.
 *
 * A type belongs to at most one group, and one another group holds is rendered
 * as a DISABLED option saying who has it. Dropping it from the list instead
 * would answer "¿por qué no está Marmita?" with silence.
 */
const AddTypeForm = ({ stationGroupId, ownTypeIds, onSaved }: AddTypeFormProps): ReactElement => {
  const [typeId, setTypeId] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const catalogQuery = trpc.stations.listWorkCenterTypes.useQuery(
    undefined,
    { refetchInterval: MIRROR_REFETCH_MS },
  )

  const addType = trpc.stations.addType.useMutation({
    onSuccess: () => {
      setTypeId('')
      onSaved()
    },
    onError: (error) => setErrorMessage(trpcMessage(error)),
  })

  const toOption = (type: CatalogType): SelectOption => {
    const label = mirrorLabel(type.code, type.title, `Tipo ${type.typeId}`)
    return {
      value: String(type.typeId),
      label: type.heldBy === null ? label : `${label} — En grupo ${type.heldBy.name}`,
      disabled: type.heldBy !== null,
    }
  }

  if (catalogQuery.isPending) {
    return <Spinner label="Cargando el catálogo de tipos…" />
  }

  if (catalogQuery.isError) {
    return <ErrorState error={catalogQuery.error} onRetry={() => void catalogQuery.refetch()} />
  }

  const options = catalogQuery.data.types
    .filter((type) => !ownTypeIds.has(type.typeId))
    .map(toOption)

  if (options.length === 0) {
    return (
      <EmptyState
        title="No queda ningún tipo por añadir"
        message="Todos los tipos disponibles ya están en este grupo."
      />
    )
  }

  return (
    <form
      className={styles['group-detail__row']}
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        setErrorMessage(null)
        addType.mutate({ stationGroupId, typeId: Number(typeId) })
      }}
    >
      <Select
        className={styles['group-detail__grow']}
        label="Añadir un tipo de estación"
        placeholder="Elige un tipo…"
        options={options}
        value={typeId}
        onChange={(event) => setTypeId(event.target.value)}
      />
      {/* The catalogue's OWN freshness, not the group's: these are two separate
          mirror reads and can be minutes apart, and this is the list a manager
          picks from. */}
      <FreshnessBadge freshness={catalogQuery.data.freshness} />
      <Button
        type="submit"
        disabled={addType.isPending || typeId === ''}
        label={addType.isPending ? 'Añadiendo…' : 'Añadir'}
      />
      {errorMessage !== null && (
        <p role="alert" className={styles['group-detail__error']}>{errorMessage}</p>
      )}
    </form>
  )
}

type GroupStationsProps = {
  stationGroupId: string
  stations: Array<ResolvedStation>
}

/**
 * The machines the group holds, besides its types. Every blast chiller is the
 * same type in MRPeasy, so racionado caliente tells its chillers apart by the
 * machines of each group.
 */
const GroupStations = ({ stationGroupId, stations }: GroupStationsProps): ReactElement => {
  const utils = trpc.useUtils()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [pendingRemoval, setPendingRemoval] = useState<ResolvedStation | null>(null)

  const invalidate = (): void => {
    void utils.stations.getGroup.invalidate({ stationGroupId })
    // The catalog carries who holds each machine: this move changed that.
    void utils.stations.listMachines.invalidate()
  }

  const removeStation = trpc.stations.removeStation.useMutation({
    onSuccess: () => {
      invalidate()
      setPendingRemoval(null)
    },
    onError: (error) => {
      setErrorMessage(trpcMessage(error))
      setPendingRemoval(null)
    },
  })

  const columns: Array<TableColumn<ResolvedStation>> = [
    {
      key: 'station',
      header: 'Máquina',
      render: (station) => (
        <span className={styles['group-detail__type']}>
          {stationLabel(station)}
          {!station.known && <Tag tone="warn" label="Sin uso en las órdenes recientes" />}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Acciones',
      align: 'right',
      render: (station) => (
        <Button
          variant="danger"
          aria-label={`Quitar ${stationLabel(station)}`}
          disabled={removeStation.isPending}
          onClick={() => {
            setErrorMessage(null)
            setPendingRemoval(station)
          }}
          label="Quitar"
        />
      ),
    },
  ]

  return (
    <article className={styles['group-detail__card']}>
      <h2 className={styles['group-detail__card-title']}>Máquinas</h2>

      {stations.length === 0
        ? (
          <EmptyState
            title="Este grupo no tiene ninguna máquina"
            message="Añade abajo las máquinas de MRPeasy que forman el grupo, si no basta con sus tipos."
          />
        )
        : (
          <Table
            caption="Máquinas del grupo"
            columns={columns}
            rows={stations}
            rowKey={(station) => String(station.stationId)}
          />
        )}

      {errorMessage !== null && (
        <p role="alert" className={styles['group-detail__error']}>{errorMessage}</p>
      )}

      <AddStationForm
        stationGroupId={stationGroupId}
        ownStationIds={new Set(stations.map((station) => station.stationId))}
        onSaved={invalidate}
      />

      <ConfirmDialog
        open={pendingRemoval !== null}
        variant="danger"
        title="Quitar la máquina del grupo"
        message={
          pendingRemoval === null
            ? ''
            : `«${stationLabel(pendingRemoval)}» dejará de estar en este grupo. Se puede volver a añadir.`
        }
        confirmLabel="Quitar"
        isLoading={removeStation.isPending}
        onConfirm={() => {
          if (pendingRemoval !== null) {
            removeStation.mutate({ stationGroupId, stationId: pendingRemoval.stationId })
          }
        }}
        onCancel={() => setPendingRemoval(null)}
      />
    </article>
  )
}

type AddStationFormProps = {
  stationGroupId: string
  ownStationIds: Set<number>
  onSaved: () => void
}

/**
 * Adding a machine from the mirror. A machine belongs to at most one group, so
 * one another group holds is a disabled option saying who has it, as with types.
 */
const AddStationForm = ({ stationGroupId, ownStationIds, onSaved }: AddStationFormProps): ReactElement => {
  const [stationId, setStationId] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const catalogQuery = trpc.stations.listMachines.useQuery(
    undefined,
    { refetchInterval: MIRROR_REFETCH_MS },
  )

  const addStation = trpc.stations.addStation.useMutation({
    onSuccess: () => {
      setStationId('')
      onSaved()
    },
    onError: (error) => setErrorMessage(trpcMessage(error)),
  })

  const toOption = (machine: CatalogMachine): SelectOption => {
    const label = mirrorLabel(machine.code, machine.title, `Máquina ${machine.stationId}`)
    return {
      value: String(machine.stationId),
      label: machine.heldBy === null ? label : `${label} — En grupo ${machine.heldBy.name}`,
      disabled: machine.heldBy !== null,
    }
  }

  if (catalogQuery.isPending) {
    return <Spinner label="Cargando las máquinas…" />
  }

  if (catalogQuery.isError) {
    return <ErrorState error={catalogQuery.error} onRetry={() => void catalogQuery.refetch()} />
  }

  const options = catalogQuery.data.machines
    .filter((machine) => !ownStationIds.has(machine.stationId))
    .map(toOption)

  if (options.length === 0) {
    return (
      <EmptyState
        title="No queda ninguna máquina por añadir"
        message="Todas las máquinas disponibles ya están en este grupo."
      />
    )
  }

  return (
    <form
      className={styles['group-detail__row']}
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        setErrorMessage(null)
        addStation.mutate({ stationGroupId, stationId: Number(stationId) })
      }}
    >
      <Select
        className={styles['group-detail__grow']}
        label="Añadir una máquina"
        placeholder="Elige una máquina…"
        options={options}
        value={stationId}
        onChange={(event) => setStationId(event.target.value)}
      />
      <FreshnessBadge freshness={catalogQuery.data.freshness} />
      <Button
        type="submit"
        disabled={addStation.isPending || stationId === ''}
        label={addStation.isPending ? 'Añadiendo…' : 'Añadir'}
      />
      {errorMessage !== null && (
        <p role="alert" className={styles['group-detail__error']}>{errorMessage}</p>
      )}
    </form>
  )
}

type GroupAssigneesProps = {
  stationGroupId: string
}

type Assignee = {
  assignmentId: string
  name: string
  since: Date
}

/**
 * Who is in this group right now, read-only: assigning and unassigning is the
 * `/asignaciones` screen's job and this one does not grow a second copy of it.
 */
const GroupAssignees = ({ stationGroupId }: GroupAssigneesProps): ReactElement => (
  <article className={styles['group-detail__card']}>
    <h2 className={styles['group-detail__card-title']}>Quién está asignado ahora</h2>
    <AssigneeList stationGroupId={stationGroupId} />
  </article>
)

/**
 * The list itself, split off the card so its four states can be early returns
 * instead of five nested ternaries under a heading.
 *
 * `listActiveByGroup` answers raw rows with no names joined in — deliberately,
 * since whoever asks already holds `assignments.listUsers` (section `grupos`,
 * so a supervisor without `admin` reads it too) — so the names are resolved
 * here. That list includes deactivated people on purpose: one can still hold
 * an open assignment, and must show as a name and not as an unknown id.
 */
const AssigneeList = ({ stationGroupId }: GroupAssigneesProps): ReactElement => {
  const assignmentsQuery = trpc.assignments.listActiveByGroup.useQuery({ stationGroupId })
  const usersQuery = trpc.assignments.listUsers.useQuery()

  const columns: Array<TableColumn<Assignee>> = [
    { key: 'name', header: 'Persona', render: (row) => row.name },
    { key: 'since', header: 'Desde', render: (row) => DAY_MONTH_YEAR.format(row.since) },
  ]

  if (assignmentsQuery.isPending || usersQuery.isPending) {
    return <Spinner label="Cargando las asignaciones…" />
  }

  if (assignmentsQuery.isError) {
    return <ErrorState error={assignmentsQuery.error} onRetry={() => void assignmentsQuery.refetch()} />
  }

  if (usersQuery.isError) {
    return <ErrorState error={usersQuery.error} onRetry={() => void usersQuery.refetch()} />
  }

  if (assignmentsQuery.data.length === 0) {
    return (
      <EmptyState
        title="Nadie asignado a este grupo"
        message="Las personas se asignan desde la pantalla de asignaciones."
      />
    )
  }

  const names = new Map(usersQuery.data.map((user) => [user.id, user.name]))
  const rows: Array<Assignee> = assignmentsQuery.data.map((row) => ({
    assignmentId: row.id,
    name: names.get(row.userId) ?? 'Persona desconocida',
    since: row.validFrom,
  }))

  return (
    <Table
      caption="Personas asignadas al grupo"
      columns={columns}
      rows={rows}
      rowKey={(row) => row.assignmentId}
    />
  )
}
